/**
 * Default Trip Checklist template editor — mirrors iOS TripTaskTemplateEditorView.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, List, message } from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DownOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { settingsEn } from '../../content/settings.en';
import {
  flushPreferencesPut,
  getUserPreferences,
  loadLocalUserPreferences,
  patchTravelerPreferenceProfile,
  type TripTaskTemplateItem,
} from '../../services/userPreferencesSyncService';
import {
  ensureDefaultTripTaskTemplate,
  seedDefaultTripTaskTemplate,
  sortedTripTaskTemplate,
} from '../../utils/tripTaskTemplate';
import styles from './Settings.module.css';

const ChecklistTemplate: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [loadingRemote, setLoadingRemote] = useState(true);
  const [items, setItems] = useState<TripTaskTemplateItem[]>(() =>
    sortedTripTaskTemplate(
      loadLocalUserPreferences().travelerPreferenceProfile.defaultTripTaskTemplate
    )
  );
  const [newTitle, setNewTitle] = useState('');

  const persistItems = useCallback((next: TripTaskTemplateItem[]) => {
    const normalized = next.map((item, sortOrder) => ({
      title: item.title,
      sortOrder,
    }));
    patchTravelerPreferenceProfile({ defaultTripTaskTemplate: normalized });
    setItems(normalized);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/signin?redirect=/settings/checklist-template', { replace: true });
      return;
    }
    let cancelled = false;
    setLoadingRemote(true);
    void getUserPreferences()
      .then((payload) => {
        if (cancelled) return;
        const template = payload.travelerPreferenceProfile.defaultTripTaskTemplate ?? [];
        if (template.length === 0) {
          setItems(ensureDefaultTripTaskTemplate());
        } else {
          setItems(sortedTripTaskTemplate(template));
        }
      })
      .catch(() => {
        if (!cancelled) {
          message.warning(settingsEn.loadError);
          setItems(ensureDefaultTripTaskTemplate());
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRemote(false);
      });
    return () => {
      cancelled = true;
      void flushPreferencesPut().catch(() => undefined);
    };
  }, [authLoading, isAuthenticated, navigate]);

  const handleAdd = (): void => {
    const title = newTitle.trim();
    if (!title) return;
    persistItems([...items, { title, sortOrder: items.length }]);
    setNewTitle('');
  };

  const handleRemove = (index: number): void => {
    persistItems(items.filter((_, i) => i !== index));
  };

  const handleMove = (index: number, delta: number): void => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [row] = next.splice(index, 1);
    next.splice(target, 0, row);
    persistItems(next);
  };

  const handleReset = (): void => {
    persistItems(seedDefaultTripTaskTemplate());
  };

  if (authLoading || (!isAuthenticated && loadingRemote)) {
    return (
      <div className={styles.settings}>
        <div className={styles.container}>
          <p className={styles.muted}>{settingsEn.checklist.title}…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      <div className={styles.container}>
        <header className={styles.header}>
          <Button
            type="link"
            className={styles.backLink}
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/settings', { replace: true })}
          >
            {settingsEn.back}
          </Button>
          <h1 className={styles.title}>{settingsEn.checklist.title}</h1>
          <p className={styles.hint}>{settingsEn.checklist.help}</p>
        </header>

        <section className={styles.section} aria-labelledby="checklist-items">
          <div className={styles.sectionTitleRow}>
            <h2 id="checklist-items" className={styles.sectionTitle}>
              {settingsEn.checklist.items}
            </h2>
            <Button type="link" disabled={loadingRemote} onClick={handleReset}>
              {settingsEn.checklist.resetDefaults}
            </Button>
          </div>

          {items.length === 0 ? (
            <p className={styles.muted}>{settingsEn.checklist.empty}</p>
          ) : (
            <List
              dataSource={items}
              renderItem={(item, index) => (
                <List.Item
                  className={styles.templateRow}
                  actions={[
                    <Button
                      key="up"
                      type="text"
                      icon={<UpOutlined />}
                      disabled={loadingRemote || index === 0}
                      aria-label={settingsEn.checklist.moveUp}
                      onClick={() => handleMove(index, -1)}
                    />,
                    <Button
                      key="down"
                      type="text"
                      icon={<DownOutlined />}
                      disabled={loadingRemote || index === items.length - 1}
                      aria-label={settingsEn.checklist.moveDown}
                      onClick={() => handleMove(index, 1)}
                    />,
                    <Button
                      key="del"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={loadingRemote}
                      aria-label={settingsEn.checklist.remove}
                      onClick={() => handleRemove(index)}
                    />,
                  ]}
                >
                  <span>{item.title}</span>
                </List.Item>
              )}
            />
          )}

          <div className={styles.addRow}>
            <Input
              value={newTitle}
              disabled={loadingRemote}
              placeholder={settingsEn.checklist.addPlaceholder}
              onChange={(e) => setNewTitle(e.target.value)}
              onPressEnter={handleAdd}
              aria-label={settingsEn.checklist.addPlaceholder}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={loadingRemote || !newTitle.trim()}
              onClick={handleAdd}
            >
              {settingsEn.checklist.add}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ChecklistTemplate;
